import React, { useState, createContext, useContext, ReactNode, useEffect, useCallback } from 'react';
import { PlpContext } from 'Context/plp-context';
import { ROUTES } from 'Utils/routes';
import { LoggedInStatus } from 'Data/constants';
import { useSDMCartContext } from '@ldp/sdm-contexts';
import useAddToCart from '@/hooks/use-add-to-cart/use-add-to-cart';
import { createCartGuidCookie, getGuid, getLoggedInStatus } from 'Utils/auth';
import useUpdateCartItemQuantity from 'GraphQL/updatecart/use-update-cart-item';
import { getCartProducts } from 'Utils/utils';
import useGetUserPcoData from '@/hooks/use-get-user-pco-data';
import { useStore } from '@/store/useStore';

type StateTuple<T> = [T, React.Dispatch<React.SetStateAction<T>>];
const noOp = () => {};

export interface INavContext {
  nav: { isMobileNavOpen: StateTuple<boolean>; isAnimating: StateTuple<boolean> };
  user: { bagCount: StateTuple<number>; userStatus: StateTuple<string>; loginURL: StateTuple<string> };
  category: {
    uid: StateTuple<string>;
    mobileUid: StateTuple<string>;
    L0: StateTuple<string>;
    L1: StateTuple<string>;
    L2: StateTuple<string>;
  };
  navId?: string;
  searchText: string;
}
export const NavContext = createContext<INavContext>({
  nav: { isMobileNavOpen: [false, noOp], isAnimating: [false, noOp] },
  user: { bagCount: [0, noOp], userStatus: ['', noOp], loginURL: ['', noOp] },
  category: {
    uid: ['', noOp],
    mobileUid: ['', noOp],
    L0: ['', noOp],
    L1: ['', noOp],
    L2: ['', noOp],
  },
  navId: 'show-plp',
  searchText: '',
});
NavContext.displayName = 'NavContext';

export const initialState = {
  mobileUid: 'FrontShopMobileShopNavNode',
  uid: 'FrontShopShopNavNode',
  L0: '',
  L1: '',
  L2: '',
  isMobileNavOpen: false,
  isAnimating: false,
  searchText: '',
};

const defaultBagcount = 0;
const defaultLoginurl = ROUTES.LOGIN;
interface Props {
  children?: ReactNode;
  navId?: string;
  state?: Partial<typeof initialState>;
}

export const NavProvider = ({ children, navId, state: givenState }: Props) => {
  const state = { ...initialState, ...givenState };

  const [userStatus, setUserStatus] = useState<string>(LoggedInStatus.ANONYMOUS);
  let bagCountValue: number = defaultBagcount;
  let loginURLValue: string = defaultLoginurl;
  if (typeof window !== 'undefined' && navId) {
    bagCountValue = Number(document.getElementById(navId)?.dataset?.bagcount) || defaultBagcount;
    loginURLValue = document.getElementById(navId)?.dataset?.loginurl || defaultLoginurl;
  }
  const [bagCount, setBagCount] = useState(bagCountValue);
  const [loginURL, setLoginURL] = useState(loginURLValue);
  const [mobileUid, setMobileUid] = useState(state.mobileUid);
  const [uid, setUid] = useState(state.uid);
  const [L0, setL0] = useState(state.L0);
  const [L1, setL1] = useState(state.L1);
  const [L2, setL2] = useState(state.L2);
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(state.isMobileNavOpen);
  const [isAnimating, setIsAnimating] = useState(state.isAnimating);
  const [cartFlag, setCartFlag] = useState(true);
  const plpContext = useContext(PlpContext);
  const searchText = plpContext?.state?.searchText || '';

  const { cartCount, selectedProduct, isUpdateAction, populateEnteries, setIsInitialCartCountUpdated } =
    useSDMCartContext();
  const { productCode, quantity } = selectedProduct ? selectedProduct : { productCode: null, quantity: 0 };
  const { addToCartData, addToCart } = useAddToCart();
  const { createUpdateCartItemQuantityMutation } = useUpdateCartItemQuantity();
  const cartData = useStore(({ cartData }) => cartData);

  const getCartData = useCallback(() => {
    const guid = getGuid();
    const loggedInStatus = getLoggedInStatus();
    return { guid, loggedInStatus };
  }, []);

  useEffect(() => {
    if (!isUpdateAction && Object.entries(selectedProduct ?? {}).length > 0) {
      invokeAddToCartMutation();
    } else if (isUpdateAction) {
      createUpdateCartItemQuantityMutation(productCode, quantity);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartCount, isUpdateAction]);

  const { guid, loggedInStatus } = getCartData();

  const invokeAddToCartMutation = () => {
    addToCart({
      variables: { productId: productCode, guid, quantity: quantity, loggedInStatus },
    });
  };
  useEffect(() => {
    const data = addToCartData && addToCartData?.addToCart?.cart;
    const guid = data && data.id;
    if (guid && getLoggedInStatus() === LoggedInStatus.ANONYMOUS) {
      createCartGuidCookie(guid);
    }
  }, [addToCartData]);

  const cartProducts = getCartProducts(cartData?.data?.entries);

  useEffect(() => {
    const userState = getLoggedInStatus();
    setUserStatus(userState);
  }, [userStatus]);

  useEffect(() => {
    if (cartProducts && cartFlag && Object.entries(selectedProduct ?? {}).length === 0) {
      populateEnteries(cartProducts);
      setCartFlag(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartProducts]);

  useEffect(() => {
    setBagCount(cartCount);
    if (!cartFlag) {
      setIsInitialCartCountUpdated(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartCount, cartFlag]);

  //getUserPcoData for analytics
  useGetUserPcoData();

  const store: INavContext = {
    navId,
    category: {
      mobileUid: [mobileUid, setMobileUid],
      uid: [uid, setUid],
      L0: [L0, setL0],
      L1: [L1, setL1],
      L2: [L2, setL2],
    },
    nav: {
      isMobileNavOpen: [isMobileNavOpen, setIsMobileNavOpen],
      isAnimating: [isAnimating, setIsAnimating],
    },
    user: {
      userStatus: [userStatus, setUserStatus],
      bagCount: [bagCount, setBagCount],
      loginURL: [loginURL, setLoginURL],
    },
    searchText,
  };

  return <NavContext.Provider value={store}>{children}</NavContext.Provider>;
};
